import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { AdminTable, type Column } from './AdminTable'
import { Pagination } from './Pagination'
import { DeleteButton } from './DeleteButton'
import { LoadingBar } from './LoadingBar'
import { Modal } from '@/components/ui/Modal'
import { useAdminTab } from '@/hooks/useAdminTab'
import { fetchAdminLanguages, createAdminLanguage, deleteAdminLanguage } from '@/api/admin'
import { LanguageFlag } from '@/components/ui/LanguageFlag'
import type { AdminLanguage } from '@/types/admin'
import { filledFill, focusRing } from '@/components/ui/buttonStyles'

interface LanguagesTabProps {
  active: boolean
  /** Where the Add Language button renders; inline above the table when absent. */
  actionSlot?: HTMLElement | null
}

export function LanguagesTab({ active, actionSlot }: LanguagesTabProps) {
  const { data, meta, loading, setPage, refresh } = useAdminTab(fetchAdminLanguages, active)
  const [modalOpen, setModalOpen] = useState(false)

  const columns: Column<AdminLanguage>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Language',
        render: (l) => (
          <span className="flex items-center gap-2">
            <LanguageFlag isoCode={l.iso_code} size="s" />
            {l.name}
          </span>
        ),
      },
      { key: 'iso', label: 'ISO Code', render: (l) => <span className="font-mono">{l.iso_code}</span> },
      { key: 'users', label: 'Users', render: (l) => l.users_count, className: 'text-right' },
      { key: 'words', label: 'Words', render: (l) => l.words_count, className: 'text-right' },
      { key: 'readings', label: 'Readings', render: (l) => l.readings_count, className: 'text-right' },
      { key: 'created', label: 'Created', render: (l) => new Date(l.created_at).toLocaleDateString() },
      {
        key: 'actions',
        label: '',
        render: (l) => (
          <DeleteButton
            label={`Delete language ${l.name}`}
            onDelete={() => deleteAdminLanguage(l.id)}
            onSuccess={refresh}
          />
        ),
        className: 'w-10',
      },
    ],
    [refresh],
  )

  if (!active) return null

  const addButton = (
    <button
      onClick={() => setModalOpen(true)}
      className={`rounded-md px-4 py-2 text-sm font-normal transition-colors duration-150 ${filledFill} ${focusRing}`}
    >
      Add Language
    </button>
  )

  return (
    <div>
      {actionSlot ? createPortal(addButton, actionSlot) : <div className="mb-4 flex justify-end">{addButton}</div>}
      {loading && <div className="mb-4"><LoadingBar /></div>}
      <AdminTable columns={columns} data={data} keyFn={(l) => l.id} />
      <Pagination meta={meta} onPageChange={setPage} />
      <AddLanguageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={refresh}
      />
    </div>
  )
}

const INPUT_CLASS =
  'w-full rounded-md border border-border-divider bg-transparent px-4 py-2.5 text-sm font-light text-body placeholder:text-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30'

function AddLanguageModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState('')
  const [isoCode, setIsoCode] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await createAdminLanguage({ name, iso_code: isoCode, description: description || undefined })
      setName('')
      setIsoCode('')
      setDescription('')
      onSuccess()
      onClose()
    } catch {
      setError('Failed to create language. Check that the ISO code is unique.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add Language">
      <form onSubmit={handleSubmit} className="space-y-4 mt-2">
        <div>
          <label htmlFor="lang-name" className="block text-xs uppercase tracking-widest text-label font-light mb-1.5">
            Name
          </label>
          <input
            id="lang-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="lang-iso" className="block text-xs uppercase tracking-widest text-label font-light mb-1.5">
            ISO Code
          </label>
          <input
            id="lang-iso"
            type="text"
            value={isoCode}
            onChange={(e) => setIsoCode(e.target.value.toLowerCase())}
            required
            maxLength={3}
            placeholder="e.g. de, fr, es"
            className={`${INPUT_CLASS} font-mono`}
          />
        </div>
        <div>
          <label htmlFor="lang-desc" className="block text-xs uppercase tracking-widest text-label font-light mb-1.5">
            Description
          </label>
          <textarea
            id="lang-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`${INPUT_CLASS} resize-none`}
          />
        </div>
        {error && <p className="text-sm text-error font-light">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-light text-muted hover:text-body transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !name || !isoCode}
            className={`rounded-md px-4 py-2 text-sm font-normal transition-colors duration-150 disabled:opacity-50 ${filledFill} ${focusRing}`}
          >
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
